// ./Pagination.tsx
import "./styles/Pagination.css";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  // Simple page range (you can make this more advanced if needed)
  const visiblePages = (() => {
    const pages: number[] = [];
    const maxButtons = 5; // keep compact for mobile

    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxButtons - 1);

    // re-adjust start if we're at the end
    start = Math.max(1, end - maxButtons + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  })();

  return (
    <nav className="pagination-root" aria-label="Job pagination">
      <button
        className="pagination-btn pagination-prev"
        onClick={() => goToPage(currentPage - 1)}
        disabled={!canGoPrev}
        aria-label="Previous page"
      >
        ‹
      </button>

      <div className="pagination-pages">
        {visiblePages[0] > 1 && (
          <>
            <button
              className="pagination-btn"
              onClick={() => goToPage(1)}
              aria-label="Page 1"
            >
              1
            </button>
            {visiblePages[0] > 2 && (
              <span className="pagination-ellipsis" aria-hidden="true">
                …
              </span>
            )}
          </>
        )}

        {visiblePages.map((page) => (
          <button
            key={page}
            className={
              "pagination-btn" +
              (page === currentPage ? " pagination-btn-active" : "")
            }
            onClick={() => goToPage(page)}
            aria-current={page === currentPage ? "page" : undefined}
          >
            {page}
          </button>
        ))}

        {visiblePages[visiblePages.length - 1] < totalPages && (
          <>
            {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
              <span className="pagination-ellipsis" aria-hidden="true">
                …
              </span>
            )}
            <button
              className="pagination-btn"
              onClick={() => goToPage(totalPages)}
              aria-label={`Page ${totalPages}`}
            >
              {totalPages}
            </button>
          </>
        )}
      </div>

      <button
        className="pagination-btn pagination-next"
        onClick={() => goToPage(currentPage + 1)}
        disabled={!canGoNext}
        aria-label="Next page"
      >
        ›
      </button>
    </nav>
  );
}
